import React, {
  createContext, useContext, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { createSlicedStore, combineSlices, useStore } from '../../store/createStore';
import { drawerSlice } from '../../store/slices/drawer';

// Scroll state lives in the store, not context — see
// .claude/context/store-slices.md "drawerSlice" for why.

const DrawerContext = createContext({
  scrollToBottom: () => {},
  onScroll: () => {},
  forceIsAtBottomCheck: () => {},
  scrollEl: null,
  drawerState: 'OPEN',
  forceCloseForm: false,
  store: null,
});

export const DrawerProvider = ({ children, drawerState = 'OPEN' }) => {
  const scrollEl = useRef(null);
  // One store per provider instance, created once.
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createSlicedStore(combineSlices(drawerSlice));
  }
  const store = storeRef.current;

  // Stable action identities (defined once) — selecting them never re-renders.
  const actionsRef = useRef(null);
  if (!actionsRef.current) {
    const isAtBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight <= 20;
    actionsRef.current = {
      scrollToBottom: (behavior = 'auto') => {
        if (scrollEl.current) {
          scrollEl.current.scrollTo({ top: scrollEl.current.scrollHeight, behavior });
        }
      },
      // 20px margin gives the bottom calculation a bit of slack.
      onScroll: (event) => store.getState().setScrolledToBottom(isAtBottom(event.target)),
      forceIsAtBottomCheck: () => {
        if (scrollEl.current) {
          store.getState().setScrolledToBottom(isAtBottom(scrollEl.current));
        }
      },
    };
  }

  const [focusInput, setFocusInput] = useState(false);

  // Context value is stable except when drawerState actually transitions (a
  // legitimate re-render), so scroll updates never invalidate it.
  const value = useMemo(() => ({
    ...actionsRef.current,
    scrollEl,
    store,
    drawerState,
    forceCloseForm: drawerState === 'MINIMIZED' || drawerState === 'CLOSED',
    focusInput,
    setFocusInput,
  }), [drawerState, store, focusInput]);

  return (
    <DrawerContext.Provider value={value}>
      {children}
    </DrawerContext.Provider>
  );
};

DrawerProvider.propTypes = {
  children: PropTypes.node.isRequired,
  drawerState: PropTypes.string,
};

// Stable drawer context: actions, scrollEl, drawerState, forceCloseForm.
// Reading these does NOT re-render on scroll.
export const useDrawer = () => useContext(DrawerContext);

// Reactive selector into the drawer store (e.g. isUserScrolledToBottom). Only
// components that call this re-render when the selected slice changes.
export const useDrawerStore = (selector) => {
  const { store } = useContext(DrawerContext);
  return useStore(store, selector);
};
